import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPermissionsDocument } from '../store/permission-v2/actions'
import { useTenantOrGlobalAdmin } from './useTenantOrGlobalAdmin'
import { DateTime } from 'luxon'
import {
  getKeyForEntityType,
  getKeyForGroup,
  getKeyForSystemPermission
} from '../components/Permissions/PermissionsEditor'

export const permissionsAvailable = {
  create: 'create',
  update: 'update',
  delete: 'delete',
  view: 'view',
  lock_children: 'lock_children'
}

const systemPermissions = {
  entityTypes: 'entity types',
  collections: 'collections',
  versionPDFs: 'version pdfs',
  adminModules: 'admin modules',
  privilegedPermissions: 'privileged permissions',
  tenantLevelPermissions: 'tenant level permissions'
}

export const useV1Permission = true
export const permissionsDebugLogging = false
export const usePermissionsV2Checker = () => {
  const permissionCache = useRef({})
  // TODO: This should all come down in user object later, we should not be doing a fetch.
  const dispatch = useDispatch()
  const { tenant, actingRole: role, user } = useTenantOrGlobalAdmin()

  const { permissionsDocument, lastRetrieveFinished } = useSelector(
    (state) => state.permissionsV2
  )

  useEffect(() => {
    if (
      lastRetrieveFinished < DateTime.local().minus({ minutes: 2 }).toSeconds()
    ) {
      dispatch(
        fetchPermissionsDocument({
          tenant: tenant?.id,
          role,
          personnel: user?.personnel_record?.id
        })
      )
    }
  }, [tenant, role, user, lastRetrieveFinished])

  const hasPermission = useCallback(
    (permission, entityType, systemPermissionName) => {
      if (!permissionsDocument) return false
      const cacheKey =
        permission + '_' + entityType?.id + '_' + systemPermissionName
      if (permissionCache.current[cacheKey] !== undefined) {
        return permissionCache.current[cacheKey]
      }

      const moduleParent = entityType?.object_data?.visible_on_calendar?.[0]
      const personnel = user?.personnel_record?.id
      const keys = [
        getKeyForGroup(moduleParent, null, null, null),
        getKeyForGroup(moduleParent, null, role, null),
        personnel ? getKeyForGroup(moduleParent, null, null, personnel) : null,

        entityType ? getKeyForEntityType(entityType, null, null, null) : null,
        entityType ? getKeyForEntityType(entityType, null, role, null) : null,
        personnel && entityType
          ? getKeyForEntityType(entityType, null, null, personnel)
          : null,
        systemPermissionName
          ? getKeyForSystemPermission(systemPermissionName, null, null, null)
          : null,
        systemPermissionName
          ? getKeyForSystemPermission(systemPermissionName, null, role, null)
          : null,

        tenant?.id && getKeyForGroup(moduleParent, tenant?.id, null, null),
        tenant?.id && getKeyForGroup(moduleParent, tenant?.id, role, null),
        tenant?.id && personnel
          ? getKeyForGroup(moduleParent, tenant?.id, null, personnel)
          : null,
        tenant?.id && entityType
          ? getKeyForEntityType(entityType, tenant?.id, null, null)
          : null,
        tenant?.id && entityType
          ? getKeyForEntityType(entityType, tenant?.id, role, null)
          : null,
        tenant?.id && systemPermissionName
          ? getKeyForSystemPermission(
              systemPermissionName,
              tenant?.id,
              null,
              null
            )
          : null,
        tenant?.id && systemPermissionName
          ? getKeyForSystemPermission(
              systemPermissionName,
              tenant?.id,
              role,
              null
            )
          : null,
        tenant?.id && personnel && entityType
          ? getKeyForEntityType(entityType, tenant?.id, null, personnel)
          : null
      ].filter((key) => key) //remove nully

      switch (permission) {
        case permissionsAvailable.create:
        case permissionsAvailable.update:
        case permissionsAvailable.delete:
        case permissionsAvailable.view:
          const keysFound = []
          let keyUsed = null
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i]
            if (permissionsDocument?.[key]) {
              keysFound.push({ key, value: permissionsDocument[key] })
              keyUsed = permissionsDocument[key]
              if (
                permissionsDocument?.[key]?.[permissionsAvailable.lock_children]
              ) {
                break
              }
            }
          }
          ;(window?.permissionsDebugging || permissionsDebugLogging) &&
            console.info(
              'Permissions:',
              permission,
              '\n',
              'EntityType:',
              entityType,
              '\n',
              'Special Permission:',
              systemPermissionName,
              '\n',
              'Relevant Keys:',
              keys,
              '\n',
              'Keys used:',
              keysFound
            )
          permissionCache.current[cacheKey] = keyUsed?.[permission] ?? false
          return keyUsed?.[permission] ?? false
        default:
          console.warn('Did not recognise permission', permission)
          return false
      }
    },
    [permissionsDocument, tenant, role, user, permissionCache]
  )

  const retObj = useMemo(() => {
    return { ...permissionsAvailable, systemPermissions, hasPermission }
  }, [permissionsAvailable, systemPermissions, hasPermission])
  return retObj
}
